import * as React from "react";

import { getImportStatus, importCalendar, listIntegrations } from "@/api/client";
import { useSession } from "@/auth/session/SessionProvider";
import { activeProviders } from "@/lib/calendarProviders";

/**
 * On a fresh login — and, as a fallback, whenever the cached import has gone
 * stale — quietly re-pull the user's connected calendar so the 30-day window shown
 * on the Delgentic calendar reflects reality without them clicking "Import my
 * events".
 *
 * <p>Render-nothing, mounted once under {@link ProfileProvider} (inside the
 * approval gate) so it runs for the whole authenticated app exactly once per
 * full page load — the SPA remounts on the OIDC login redirect. It's a no-op
 * unless the user has an ACTIVE calendar connection, and it's
 * best-effort: any failure is swallowed since the manual button remains.</p>
 *
 * <p>The "fresh login" signal is a sessionStorage flag raised by
 * {@code oidcConfig.markFreshLogin()} — from {@code onSigninCallback} after a
 * redirect sign-in, and from the session facade after a native one. Without it
 * we can't tell a real login from a plain reload, so a stale-threshold guard
 * keeps reloads from re-importing on every page load.</p>
 */
const FRESH_LOGIN_FLAG = "a2a.calendarReimportPending"; // set in auth/oidcConfig.ts
const STALE_AFTER_MS = 6 * 60 * 60 * 1000; // 6h — long-lived sessions still refresh on reopen.

export function CalendarLoginSync() {
  const session = useSession();
  const ran = React.useRef(false);

  React.useEffect(() => {
    if (!session.isAuthenticated || ran.current) return;
    ran.current = true;
    void maybeReimport();
  }, [session.isAuthenticated]);

  return null;
}

async function maybeReimport(): Promise<void> {
  try {
    const integrations = await listIntegrations();
    const connected = activeProviders(integrations);
    if (connected.length === 0) return;

    if (!consumeFreshLoginFlag()) {
      // Not a fresh login (a reload or deep-link) — only refresh when the cache
      // is genuinely stale, so repeated reloads don't re-import every time.
      const status = await getImportStatus().catch(() => null);
      const last = status?.lastImportedAt ? Date.parse(status.lastImportedAt) : 0;
      if (last && Date.now() - last < STALE_AFTER_MS) return;
    }

    for (const provider of connected) {
      await importCalendar(provider);
    }
  } catch {
    // Best-effort: the manual "Import my events" button is always available.
  }
}

function consumeFreshLoginFlag(): boolean {
  try {
    const pending = window.sessionStorage.getItem(FRESH_LOGIN_FLAG);
    if (pending) window.sessionStorage.removeItem(FRESH_LOGIN_FLAG);
    return pending === "1";
  } catch {
    return false;
  }
}
